import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Frame, Page, Loading } from '@shopify/polaris';
import createApp from '@shopify/app-bridge';
import { getSessionToken } from '@shopify/app-bridge-utils';

import { handleError } from '../../utils/errorHandling';
import { checkMerchantOnboarded } from '../../requests';
import useMessage from '../../hooks/useMessage';
import { getAppConfig, getShop } from '../../utils/misc';

type RedirectTarget = 'settings' | 'onboarding';

const appConfig = getAppConfig();

const SelectInitialRoute = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState<RedirectTarget | null>(null);
  const { bannerMarkup, showMessage } = useMessage();

  /**
   * To know which page (settings or onboarding) should be shown when merchant opens
   * the app, request to get merchant's settings is fired. If response status is 404,
   * then merchant is presented with onboarding screen.
   */
  useEffect(() => {
    const determineInitialRoute = async () => {
      try {
        setIsLoading(true);

        if (!appConfig) {
          return;
        }

        const app = createApp(appConfig);
        const token = await getSessionToken(app)
          .then((token: string) => token)
          .catch((err: unknown) => {
            handleError(err, showMessage, navigate);
            return "";
          });

        if (!token) {
          return;
        }

        const shopDomain = getShop(token);
        
        if (shopDomain) {
          await checkMerchantOnboarded(shopDomain);
          setRedirectTarget('settings');
        }
      } catch (err) {
        // @ts-ignore
        if (err?.response?.status === 404) {
          setRedirectTarget('onboarding');
        } else {
          handleError(err, showMessage, navigate);
        }
      } finally {
        setIsLoading(false);
      }
    };

    determineInitialRoute();
  }, [showMessage, navigate]);



  if (redirectTarget === 'settings') {
    return (
      <Navigate to="/settings/edit" replace/>
    );
  } else if (redirectTarget === 'onboarding') {
    return (
      <Navigate to="/onboarding" replace/>
    );
  }

  return (
    <Frame>
      {isLoading && <Loading />}

      <Page narrowWidth>
        {bannerMarkup}
      </Page>
    </Frame>
  );
};


export default SelectInitialRoute;
